/** @jsx jsx */

import React, {
  useContext,
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
} from 'react';
import { createPortal } from 'react-dom';
import { css, jsx } from '@emotion/core';
import ie11VerticalFlexCenterFix from 'services/ie11VerticalFlexCenterFix';
import getStore from 'services/getStore';
import pubSub from 'services/pubSub';
import checkReturnToInteractionEditing from 'services/checkReturnToInteractionEditing';
import getBlockById from 'services/apiHelpers/getBlockById';
import { useSelector } from 'react-redux';
import setCurrentBlock from 'services/apiHelpers/setCurrentBlock';
import updateReadOnlyText from 'services/updateReadOnlyText';

const store = getStore();

//

let container;
let modalOpen = false;

interface IModal {
  minWidth?: string;
  onClose?: () => void;
  children: any;
}

const Modal = ({ minWidth = '50vw', onClose, children }: IModal) => {
  if (!container) container = document.querySelector('#modal-container');

  const [btnColor, setBtnColor] = useState(undefined);

  const focusedNodeIdOnOpen = useSelector<any>(
    ({ focusedNodeId }) => focusedNodeId
  );

  const rootContainer = useRef(null);
  const contentContainer = useRef(null);
  const hiddenButton = useRef(null);
  const buttonTabIndex = useRef(0);
  const checkResize = useRef(false);

  useEffect(() => {
    modalOpen = true;
    window['MODAL_OPEN'] = true;

    // if (window.dev && !window.dev()) {
    //   console.log('modal has focus'.toUpperCase());
    // rootContainer.current.focus();
    contentContainer.current.focus();

    const content: any = contentContainer.current.querySelector(
      '.chameleon-text'
    );

    if (content) {
      content.setAttribute('tabindex', '0');
      content.focus();
    }

    let button: any = contentContainer.current.querySelector('button');
    button?.setAttribute('aria-label', 'Close dialog');

    window.addEventListener('keydown', tabOverride);
    // }

    // give ALL other elements aria-hidden ???
    const root = document.querySelector('#root');
    root.setAttribute('aria-hidden', 'true');

    document.body.style.overflowY = 'hidden';

    const onResize = () => {
      if (!rootContainer.current) return;

      rootContainer.current.style.alignItems =
        window.innerHeight < contentContainer.current.scrollHeight
          ? 'flex-start'
          : 'center';

      checkResize.current = true;
    };

    const tick = setInterval(() => {
      onResize();
      window.addEventListener('resize', onResize);
      if (checkResize.current) clearInterval(tick);
    }, 100);

    function tabOverride(e) {
      const content: any = contentContainer.current.querySelector(
        '.chameleon-text'
      );

      button = contentContainer.current.querySelector('button');
      const buttonBackgroundColor = window.getComputedStyle(button)
        .backgroundColor;

      // button.setAttribute('aria-label', 'Close dialog');

      if (!btnColor) {
        setBtnColor(buttonBackgroundColor);
      }

      if (e.key === 'Tab') {
        e.preventDefault();

        // if (buttonTabIndex.current === 0) {
        //   buttonTabIndex.current = 1;
        //   button.focus();
        // } else {
        //   buttonTabIndex.current = 0;
        //   hiddenButton.current.focus();
        // }

        const elements = [content, button, hiddenButton.current];

        // console.log({ elements });

        elements[
          (buttonTabIndex.current =
            (buttonTabIndex.current + 1) % elements.length)
        ].focus();
      }
    }

    return () => {
      modalOpen = false;
      window['MODAL_OPEN'] = false;

      root.setAttribute('aria-hidden', 'false');
      document.body.style.overflowY = 'auto';

      checkResize.current = false;
      window.removeEventListener('keydown', tabOverride);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  useEffect(() => {
    if (!window.dev) {
      // we're opening the modal from the editor so we don't need these checks
      return;
    }

    pubSub.subscribe('CLOSE_MODAL', $ => {
      onClose();
    });

    return () => {
      const { focusedNodeId } = store.getState();

      if (!focusedNodeId) {
        return;
      }

      const block = getBlockById(focusedNodeId);

      const isNotInteraction = [
        'text',
        'image',
        'video',
        'svg',
        'embed',
        'editablebutton',
        'continuebutton',
      ].includes(block.type.toLowerCase());

      // console.log({ 'block?.meta?.subType': block?.meta?.subType });

      if (isNotInteraction) {
        if (focusedNodeIdOnOpen) {
          setCurrentBlock({ id: focusedNodeIdOnOpen });
        } else {
          setCurrentBlock(null);
        }
      }
    };
  }, []);

  useLayoutEffect(() => {
    if (window.dev && !window.dev()) {
      updateReadOnlyText(contentContainer.current);
    }
  }, []);

  useEffect(() => {
    if (window.dev && window.dev()) {
      return;
    }

    // find all the hrefs a

    const links = Array.from(contentContainer.current.querySelectorAll('a'));

    if (!links.length) {
      return;
    }

    const closeOnLinkClick = e => {
      onClose();
    };

    const addRemoveCloseOnLinkClick = addOrRemove => {
      links.forEach(link => {
        if (link.href.includes('#')) {
          link[`${addOrRemove}EventListener`]('click', closeOnLinkClick);
        }
      });
    };

    addRemoveCloseOnLinkClick('add');

    return () => {
      addRemoveCloseOnLinkClick('remove');
    };
  }, []);

  return createPortal(
    <div
      ref={rootContainer}
      className={!modalOpen ? 'fader_-5s' : ''}
      css={css`
        position: fixed;
        display: flex;
        justify-content: center;
        align-items: center;
        left: 0px;
        top: 0px;
        width: 100vw;
        min-height: 100vh;
        max-height: 100%;
        padding: 10px 10px;
        background: rgba(0, 0, 0, 0.6);
        z-index: 1;
        ${ie11VerticalFlexCenterFix('height: 100vh;')}

        overflow-y: scroll;
      `}
      // onClick={onClose}
    >
      <button
        ref={hiddenButton}
        css={css`
          position: absolute;
          left: 3px;
          top: 3px;
          width: calc(100% - 6px);
          height: calc(100% - 6px);

          border: none;
          outline: none;
          background: transparent;

          &:focus {
            border: 1px solid ${btnColor || '#282a2b'};
          }
        `}
        onClick={(e: any) => {
          // console.log(e.detail, onClose);
          onClose();
        }}
        aria-hidden="true"
      ></button>

      <div
        ref={contentContainer}
        css={css`
          position: relative;
          display: flex;
          justify-content: center;
          align-items: center;
          // min-width: ${minWidth};
          // min-height: 50vh;
          // max-width: 608px;
          width: 100%;
          max-width: 1024px;
          border-radius: 10px;
          background: #fff;
          box-shadow: 0 0 12px 0 rgba(0, 0, 0, 0.2);

          // .chameleon-text {
          //   &:focus {
          //     border: 1px solid lime !important;
          //   }
          // }
        `}
        onClick={e => e.stopPropagation()}
        // tabIndex={0}
      >
        {children}
      </div>
    </div>,
    container
  );
};

const useModal = ({ onClose, children }) => {
  const [open, setState] = useState(true);

  return <Modal onClose={() => setState(false)}>{children}</Modal>;
};

export default Modal;

export { useModal };
